import {
  ArrowLeft,
  ArrowLeftRight,
  ClipboardCheck,
  ClipboardList,
  CreditCard,
  DoorOpen,
  LockKeyhole,
  ReceiptText,
  RefreshCw,
  Tags,
  UnlockKeyhole,
  Undo2,
} from 'lucide-react'
import { useState, type ReactNode } from 'react'

import { Button } from '@krp/ui-kit'

import { QueryState } from '@krp/sale-ui'
import type { SaleActions, AqsiSaleVm } from '../../sale/model/types'
import { RmkDialog } from '@krp/sale-ui'
import type { RmkActions, RmkVm } from '../model/types'
import { FeedbackHelp } from './FeedbackHelp'
import { OperationPanel } from './OperationPanel'
import { PriceTypePicker } from './PriceTypePicker'

/**
 * «··· Действия» — всё, что кассиру нужно РЕДКО, за одной кнопкой (как в
 * старом РМК): плитки крупные — у прилавка работают стоя и пальцем. Смена
 * открывается и закрывается здесь же, итог Z-отчёта виден на месте; «Чеки» —
 * окно чеков этой кассы поверх РМК (возврат — из карточки чека).
 */
export function RmkActionsDialog({
  rmk,
  rmkActions,
  sale,
  saleActions,
  onClose,
}: {
  rmk: RmkVm
  rmkActions: RmkActions
  sale: AqsiSaleVm
  saleActions: SaleActions
  onClose: () => void
}) {
  const [view, setView] = useState<'tiles' | 'shift' | 'price' | 'refund'>('tiles')
  const mine = rmk.mine
  if (mine === null) return null
  const run = (action: () => void) => () => {
    action()
    onClose()
  }
  const shiftHint = rmk.shift?.isOpen === true ? 'Закрыть смену, Z-отчёт' : rmk.shift?.isOpen === false ? 'Открыть смену' : 'Проверить смену'

  // Вид цены меняется между чеками: посреди чека служба откажет, поэтому выбор закрыт сразу и словами.
  const priceLocked = sale.saleId !== null ? 'Вид цены меняется между чеками: закончите или отмените текущий чек.' : null

  if (view === 'price' && rmk.priceTypes !== null) {
    return (
      <RmkDialog title="Вид цены" size="lg" onClose={onClose}>
        <div className="rmk-shift-panel">
          <Button variant="ghost" icon={<ArrowLeft size={16} aria-hidden="true" />} onClick={() => setView('tiles')}>
            Действия
          </Button>
          <PriceTypePicker priceTypes={rmk.priceTypes} busy={rmk.busy} lockedReason={priceLocked} onChoose={rmkActions.choosePriceType} onRetry={rmkActions.reload} />
        </div>
      </RmkDialog>
    )
  }

  if (view === 'refund' && rmkActions.openRefundSearch !== undefined && rmkActions.openSlipRefund !== undefined) {
    return (
      <RmkDialog title="Возврат" size="lg" onClose={onClose}>
        <div className="rmk-shift-panel">
          <Button variant="ghost" icon={<ArrowLeft size={16} aria-hidden="true" />} onClick={() => setView('tiles')}>
            Действия
          </Button>
          <div className="rmk-actions__tiles">
            <Tile
              icon={<CreditCard size={22} aria-hidden="true" />}
              title="Возврат по слип-чеку"
              hint="Оплата прошла на терминале, а фискальный чек не пробит"
              onClick={run(rmkActions.openSlipRefund)}
            />
            <Tile
              icon={<Undo2 size={22} aria-hidden="true" />}
              title="Стандартный возврат"
              hint="Найти чек и вернуть по нему"
              onClick={run(rmkActions.openRefundSearch)}
            />
          </div>
        </div>
      </RmkDialog>
    )
  }

  if (view === 'shift') {
    return (
      <RmkDialog title="Смена" size="lg" onClose={onClose}>
        <ShiftPanel rmk={rmk} actions={rmkActions} onBack={() => setView('tiles')} />
      </RmkDialog>
    )
  }

  return (
    <RmkDialog title="Действия" size="lg" onClose={onClose}>
      <div className="rmk-actions">
        <section aria-label="Разделы рабочего места">
          <div className="rmk-actions__tiles">
            <Tile icon={<ClipboardList size={22} aria-hidden="true" />} title="Смена" hint={shiftHint} onClick={() => setView('shift')} />
            {rmk.priceTypes !== null && (
              <Tile icon={<Tags size={22} aria-hidden="true" />} title="Вид цены" hint={mine.priceTypeName} onClick={() => setView('price')} />
            )}
            <Tile icon={<ReceiptText size={22} aria-hidden="true" />} title="Чеки" hint="Чеки кассы, возврат" onClick={run(rmkActions.openReceipts)} />
            {rmkActions.openRefundSearch !== undefined && rmkActions.openSlipRefund !== undefined && (
              <Tile icon={<Undo2 size={22} aria-hidden="true" />} title="Возврат" hint="По чеку или по слип-чеку" onClick={() => setView('refund')} />
            )}
            {rmkActions.openPrintSettings && <Tile icon={<ReceiptText size={22} aria-hidden="true" />} title="Настройки РМК" hint="Оплата по QR-коду (СБП) и копии слипа этой кассы" onClick={run(rmkActions.openPrintSettings)} />}
            {sale.canMarkIssuedOutside && (
              <Tile
                icon={<ClipboardCheck size={22} aria-hidden="true" />}
                title="Чек пробит на кассе"
                hint="Закрыть продажу чеком с кассы"
                onClick={run(saleActions.openIssuedOutside)}
              />
            )}
            {rmk.source.alternatives > 1 && (
              <Tile icon={<ArrowLeftRight size={22} aria-hidden="true" />} title="Другой источник" hint="Вернуться к выбору источника" onClick={run(rmkActions.switchSource)} />
            )}
          </div>
        </section>

        <FeedbackHelp help={rmk.feedbackHelp} onIncidents={run(rmkActions.openIncidents)} />

        <section className="rmk-actions__where" aria-label="Где вы работаете">
          <h3>Где вы работаете</h3>
          <dl className="aqsi-facts">
            <dt>Касса</dt>
            <dd>
              {mine.registerName}
              {mine.serial !== '' && ` (ККТ ${mine.serial})`}
            </dd>
            <dt>Источник</dt>
            <dd>{rmk.source.name}</dd>
            <dt>Вид цены</dt>
            <dd>{mine.priceTypeName}</dd>
            <dt>Начало работы</dt>
            <dd>{mine.openedAt}</dd>
            <dt>Смена</dt>
            <dd>
              {rmk.shift?.isOpen === true
                ? `открыта${rmk.shift.number !== null ? `, №${rmk.shift.number}` : ''}`
                : rmk.shift?.isOpen === false
                  ? 'закрыта'
                  : 'не подтверждена'}
              {mine.adoptedOutside && ' (принята — открыта вне РМК)'}
            </dd>
          </dl>
          <div className="aqsi-card__actions">
            {/* Окно не закрывается: отказ службы (например, неизвестный исход продажи) виден здесь же; ушли — РМК сам вернётся к выбору кассы. */}
            <Button icon={<DoorOpen size={16} aria-hidden="true" />} disabled={rmk.busy} onClick={rmkActions.closeSession}>
              Закончить работу и выбрать кассу
            </Button>
          </div>
          {rmk.shift?.isOpen === true && (
            <p className="aqsi-card__text">Смена останется открытой: её закрывает Z-отчёт («Смена» → «Закрыть смену»), а не уход от кассы.</p>
          )}
        </section>
      </div>
    </RmkDialog>
  )
}

function Tile({
  icon,
  title,
  hint,
  disabled = false,
  onClick,
}: {
  icon: ReactNode
  title: string
  hint: string
  disabled?: boolean
  onClick: () => void
}) {
  return (
    <button type="button" className="rmk-tile" disabled={disabled} onClick={onClick}>
      <span className="rmk-tile__icon">{icon}</span>
      <span className="rmk-tile__title">{title}</span>
      <span className="rmk-tile__hint">{hint}</span>
    </button>
  )
}

/** Смена на месте, как в старом РМК: состояние, команды и итог Z-отчёта после закрытия. */
function ShiftPanel({ rmk, actions, onBack }: { rmk: RmkVm; actions: RmkActions; onBack: () => void }) {
  const [confirmClose, setConfirmClose] = useState(false)
  const shift = rmk.shift
  const commands = rmk.shiftCommands
  return (
    <div className="rmk-shift-panel">
      <Button variant="ghost" icon={<ArrowLeft size={16} aria-hidden="true" />} onClick={onBack}>
        Действия
      </Button>
      {shift !== null && <QueryState requestIdHidden query={shift.query} what="Смена" emptyText="Смена не прочитана" onRetry={actions.readShift} />}
      {shift !== null && shift.isOpen !== null && (
        <>
          <p className="aqsi-card__state">
            {shift.isOpen ? <UnlockKeyhole size={16} aria-hidden="true" /> : <LockKeyhole size={16} aria-hidden="true" />}
            {shift.message}
          </p>
          {shift.facts.length > 0 && (
            <dl className="aqsi-facts" aria-label={shift.isOpen ? 'Смена' : 'Z-отчёт'}>
              {shift.facts.map((fact) => (
                <div key={fact.label} style={{ display: 'contents' }}>
                  <dt>{fact.label}</dt>
                  <dd>{fact.value}</dd>
                </div>
              ))}
            </dl>
          )}
          {shift.problems.length > 0 && (
            <ul aria-label="Замечания кассы">
              {shift.problems.map((problem) => (
                <li key={problem}>{problem}</li>
              ))}
            </ul>
          )}
        </>
      )}
      {confirmClose ? (
        <div className="aqsi-panel" data-tone="warning" role="group" aria-label="Закрыть смену?">
          <p className="aqsi-card__text">Закрыть смену? Касса напечатает Z-отчёт; продавать можно будет после открытия новой смены.</p>
          <div className="aqsi-card__actions">
            <Button
              variant="danger"
              onClick={() => {
                actions.closeShift()
                setConfirmClose(false)
              }}
            >
              Закрыть смену
            </Button>
            <Button variant="ghost" onClick={() => setConfirmClose(false)}>
              Отмена
            </Button>
          </div>
        </div>
      ) : (
        <div className="aqsi-card__actions rmk-gate__actions">
          {commands.canOpen && (
            <Button variant="primary" actionId="aqsi.rmk.open-shift" disabled={rmk.busy} onClick={actions.openShift}>
              Открыть смену
            </Button>
          )}
          {commands.canAdopt && (
            <Button variant="primary" actionId="aqsi.rmk.continue-shift" disabled={rmk.busy} onClick={actions.adoptShift}>
              Принять смену
            </Button>
          )}
          {commands.canClose && <Button onClick={() => setConfirmClose(true)}>Закрыть смену</Button>}
          <Button icon={<RefreshCw size={14} aria-hidden="true" />} onClick={actions.readShift}>
            Проверить смену на кассе
          </Button>
        </div>
      )}
      {commands.blockedReason !== null && !commands.canAdopt && <p className="aqsi-card__text">{commands.blockedReason}</p>}
      {rmk.operation !== null && (
        <OperationPanel
          operation={rmk.operation}
          onCheck={actions.checkOperation}
          onReconcile={actions.reconcileOperation}
          onDismiss={actions.dismissOperation}
        />
      )}
    </div>
  )
}
